import { useCallback, useEffect, useRef, useState } from 'react';
import { getHashFromURL } from '@noths/polaris-client-utils';

import { REVIEWS_DRAWER_URL_FRAGMENT_ID } from 'src/fragmentIDs';

export interface ReviewsDrawerFragment {
  /** Drops the review id from the URL without closing the drawer or adding a history entry. */
  clearReviewId: () => void;
  /** Leaves the history entries the drawer owns, which is what closes it. */
  close: () => void;
  isOpen: boolean;
  /** The review a `#reviews-drawer-{id}` link asked for, if any. */
  reviewId: string | null;
}

type Fragment = Pick<ReviewsDrawerFragment, 'isOpen' | 'reviewId'>;

const CLOSED: Fragment = { isOpen: false, reviewId: null };
const REVIEW_ID_PREFIX = `${REVIEWS_DRAWER_URL_FRAGMENT_ID}-`;

const readFragment = (url: string): Fragment => {
  const hash = getHashFromURL(url);

  // The list fragment exactly or the review prefix, so an unrelated anchor that merely starts
  // with the same letters does not open the drawer.
  if (!hash || (hash !== REVIEWS_DRAWER_URL_FRAGMENT_ID && !hash.startsWith(REVIEW_ID_PREFIX))) {
    return CLOSED;
  }

  // Feefo ids are opaque strings: not parsed as a number, and cut off the known prefix rather
  // than split on it, so an id that happens to contain the prefix survives the round trip.
  const id = hash.startsWith(REVIEW_ID_PREFIX) ? hash.slice(REVIEW_ID_PREFIX.length) : '';

  return { isOpen: true, reviewId: id || null };
};

/**
 * How many history entries the drawer owns after a hash navigation: the one that opened it,
 * plus one for a review opened from its list. Zero when the page was loaded with the fragment
 * already on the URL, since that entry is not the drawer's to step back over.
 */
const nextDepth = (depth: number, from: Fragment, to: Fragment) => {
  if (!to.isOpen) {
    return 0;
  }

  if (!from.isOpen) {
    return 1;
  }

  // Both open: an id appearing is a card link pushing an entry, one disappearing is the
  // browser stepping back over it. `clearReviewId` replaces the entry, so it fires no event.
  if (to.reviewId && !from.reviewId) {
    return depth + 1;
  }

  // Zero when the list entry was the deep-linked one the page loaded with.
  if (!to.reviewId && from.reviewId) {
    return Math.max(0, depth - 1);
  }

  return depth;
};

/**
 * Reads `#reviews-drawer` and `#reviews-drawer-{id}` off the URL. The URL is the source of
 * truth for open state, so closing steps back over the drawer's own history entries and the
 * browser's back button closes the drawer too. A customer who arrives on a shared link has no
 * such entry, so for them closing replaces the fragment in place rather than leaving the page.
 * Read on mount as well as on `hashchange`, because arriving with the fragment fires no event.
 */
export const useReviewsDrawerFragment = (): ReviewsDrawerFragment => {
  const [fragment, setFragment] = useState<Fragment>(CLOSED);
  // Mirrors the state with the depth, so the handlers and `close` read it without re-binding.
  const current = useRef<Fragment & { depth: number }>({ ...CLOSED, depth: 0 });
  const closing = useRef(false);

  const apply = useCallback((next: Fragment, depth: number) => {
    current.current = { ...next, depth };
    setFragment(next);
  }, []);

  // The router's own state stays on the entry, so a later back to it still renders the page.
  const dropFragment = useCallback(() => {
    const url = new URL(window.location.href);

    url.hash = '';
    window.history.replaceState(window.history.state, '', url.toString());
    apply(CLOSED, 0);
  }, [apply]);

  useEffect(() => {
    const handleHashChange = ({ newURL }: Pick<HashChangeEvent, 'newURL'>) => {
      const next = readFragment(newURL);
      const { depth, ...from } = current.current;

      apply(next, nextDepth(depth, from, next));
    };

    // Landing after `close`: when the first entry was the page's own deep-linked one, the
    // fragment is still on it and fires no `hashchange`, so it is dropped here.
    const handlePopState = () => {
      if (!closing.current) {
        return;
      }

      closing.current = false;

      if (readFragment(window.location.href).isOpen) {
        dropFragment();
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handlePopState);
    apply(readFragment(window.location.href), 0);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [apply, dropFragment]);

  const close = useCallback(() => {
    const { depth } = current.current;

    if (depth === 0) {
      dropFragment();

      return;
    }

    // Back over the drawer's entries rather than clearing the hash, so no dead entry is left
    // behind for the back button to land on.
    closing.current = true;
    window.history.go(-depth);
  }, [dropFragment]);

  const clearReviewId = useCallback(() => {
    const { depth } = current.current;

    // A review opened from the list pushed its own entry, so step back over it: replacing it
    // would leave two list entries for the back button to cross. The `hashchange` updates the
    // state and depth.
    if (depth >= 2) {
      window.history.back();

      return;
    }

    const url = new URL(window.location.href);

    url.hash = REVIEWS_DRAWER_URL_FRAGMENT_ID;
    // The review entry is the one the drawer opened on, or the page loaded with, so it is
    // replaced rather than stepped over, which would close the drawer or leave the page.
    window.history.replaceState(window.history.state, '', url.toString());
    apply({ isOpen: true, reviewId: null }, depth);
  }, [apply]);

  return { clearReviewId, close, isOpen: fragment.isOpen, reviewId: fragment.reviewId };
};
